import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ColluderRestrictions,
  EMPTY_COLLUDER_RESTRICTIONS,
  IntegrityBlockResponse
} from 'types/colluder';

// EN-10064 — client state for the colluder-block feature:
//   • `restrictions` — the viewer's advisory lobby markers (full-set replace
//     on every REST load / `gotColluderRestrictions` push).
//   • `block` — the cash-table hard-block modal, opened from the engine's
//     `gotJoinGame` INTEGRITY_RESTRICTION response.
// No-disclosure (BR-3): nothing here ever stores the opponent identity, the
// reason, or who flagged them — the server payloads are deliberately ID-only.

export interface IntegrityBlockState {
  open: boolean;
  /** Generic integrity copy from the engine (`response.error`). */
  message: string;
  /** The cash gameId the block applies to (drives the Join Waitlist CTA). */
  gameId: string;
  /** Game type ('texas' | 'omaha') for routing the waitlist join; '' if absent. */
  gameType: string;
  waitlistAvailable: boolean;
  /** Surface the block applies to ('cash' | 'tournament') — picks the modal title. */
  scope: string;
}

interface ColluderState {
  restrictions: ColluderRestrictions;
  block: IntegrityBlockState;
}

const initialBlock: IntegrityBlockState = {
  open: false,
  message: '',
  gameId: '',
  gameType: '',
  waitlistAvailable: false,
  scope: ''
};

const initialState: ColluderState = {
  restrictions: { ...EMPTY_COLLUDER_RESTRICTIONS },
  block: { ...initialBlock }
};

export const colluderRestrictionsSlice = createSlice({
  name: 'colluderRestrictions',
  initialState,
  reducers: {
    // Replace the marker state wholesale — each push/REST response is the full
    // current set, not a diff, so an empty scope clears that scope's markers.
    replaceColluderRestrictions: (state, action: PayloadAction<ColluderRestrictions>) => {
      state.restrictions = action.payload;
    },
    showIntegrityBlock: (state, action: PayloadAction<IntegrityBlockResponse>) => {
      const r = action.payload || {};
      state.block = {
        open: true,
        message: typeof r.error === 'string' ? r.error : '',
        gameId: r.gameId !== undefined && r.gameId !== null ? String(r.gameId) : '',
        gameType: typeof r.gameType === 'string' ? r.gameType : '',
        waitlistAvailable: r.waitlistAvailable === true,
        scope: typeof r.scope === 'string' ? r.scope : ''
      };
    },
    dismissIntegrityBlock: (state) => {
      state.block = { ...initialBlock };
    }
  }
});

export const { replaceColluderRestrictions, showIntegrityBlock, dismissIntegrityBlock } =
  colluderRestrictionsSlice.actions;

export default colluderRestrictionsSlice.reducer;
